import type {
  CellClassParams,
  ColDef,
  ITooltipParams,
  ValueFormatterParams,
  ValueGetterParams,
  ValueParserParams,
} from "ag-grid-community";
import { darken, readableColor, saturate } from "polished";

import { getCssColor } from "@/app-utils/color.ts";
import { cellParamsMatchesAttributeTypes, isMultiValue, isRepresenterOrUsageAttribute } from "@/app-utils/sample.ts";
import type { Dictionary } from "@/app-utils/type.ts";
import type { Attribute, AttributeValue } from "@/graphql.ts";
import type { ITableColumn } from "@/redux/modules/table-data.ts";
import {
  ATTRIBUTE_TYPE,
  BEST_HIT_COLUMN_WIDTH,
  BLAST_RESULT_FIELDS,
  CELL_TYPE,
  COLUMN_DYNAMIC_TYPES,
} from "@/settings/settings.ts";
import { CHECKBOX_COLUMN } from "@/ui-modules/core/grid/settings.ts";
import type { IColumnDef, ICustomColDef } from "./DataTable.js";

class MissingCellDataError extends Error {}

/**
 * Return the column definition props for the given table column (using a list of function e.g. using columnDef.field)
 */
export const getColumnProps = (column: Partial<ITableColumn>): ColDef => {
  return columnPropsMappers.reduce((props: ColDef, mapper: IColumnPropsMapping) => {
    const colDefProps: ColDef = mapper(column);
    if (colDefProps) {
      return { ...props, ...colDefProps };
    }
    return props;
  }, {});
};

/**
 * Sets the column definition props for the provided path
 * @param path a string representing the path to the value in the context of the full row data object (=> columnDef.field)
 * @param props custom properties of the column
 */
// compare function generator for field comparison
const cellFieldToColumnProps = (field: string, props: ColDef) => (column: Partial<ITableColumn>) =>
  column.field === field ? props : null;

const dynamicTrackerAttributeToColumnProps = (props: ColDef) => (column: Partial<ITableColumn>) =>
  column.dynamic === true &&
  column.dynamicType === COLUMN_DYNAMIC_TYPES.TRACKER_ATTRIBUTE &&
  column.cellType !== CELL_TYPE.FILE
    ? props
    : null;

const trackerAttributeTypeToColumnProps =
  (attributeType: ATTRIBUTE_TYPE, props: ColDef) => (column: Partial<ITableColumn>) =>
    column.attribute?.attributeType?.id === attributeType ? props : null;

// used in defaultColDef prop, see https://www.ag-grid.com/javascript-grid-column-definitions/#default-column-definitions
export const defaultValueGetter = (params: ValueGetterParams) => {
  const colDef: ICustomColDef = params.column.getColDef();

  return traverseAndCollect(
    params.data,
    //params.context?.editMode && colDef.editable ? `${colDef.colId}` : colDef.field,
    colDef.field,
    false,
  );
};

// by default the formatted value of an array is a joined string ; since the generic TableCellText takes into account the formattedValue we rather need the raw data
export const defaultValueFormatter = (params: ValueFormatterParams) => params.value;

type IColumnPropsMapping = (column: Partial<ITableColumn>) => ColDef;

/**
 * List of callback mappers from a table column (ITableColumn) to ColDef props.
 * The props are cumulative (using reduce function) whenever the callback returns a props object.
 */
const columnPropsMappers: IColumnPropsMapping[] = [
  dynamicTrackerAttributeToColumnProps({
    cellStyle: (params: CellClassParams) => {
      const groupField = (params.colDef as ICustomColDef).groupField;
      const values: AttributeValue[] = traverseAndCollect(params.data, groupField);
      const backgroundColor = values?.[0]?.color ? getCssColor(values[0].color) : undefined;
      const color = backgroundColor ? readableColor(backgroundColor, "#242526", "#ffffff") : undefined;
      const borderColor = backgroundColor ? darken(0.15, saturate(0.1, backgroundColor)) : undefined;
      const boxShadow = borderColor ? `inset 0 0 0 2px white, inset 0 0 0 3px ${borderColor}` : undefined;

      return values?.length
        ? {
            // borderRadius: backgroundColor ? 5 : undefined,
            backgroundColor,
            color,
            boxShadow,
          }
        : undefined;
    },
  }),

  /* ADDITIVE MATCH */
  cellFieldToColumnProps("name", { minWidth: 130, width: 200 }),
  cellFieldToColumnProps("filename", { minWidth: 200, width: 300 }),
  cellFieldToColumnProps("file", { minWidth: 200, width: 300 }),
  cellFieldToColumnProps("refVariant.name", { minWidth: 110, width: 130 }),
  cellFieldToColumnProps("accession", { width: 130 }),
  cellFieldToColumnProps("attributeName", { width: 300 }),
  cellFieldToColumnProps("description", { width: 200 }),
  cellFieldToColumnProps("database.mnemonic", { minWidth: 120, width: 150 }),
  cellFieldToColumnProps("genome.name", { minWidth: 120, width: 150 }),
  cellFieldToColumnProps("genome.species.name", { width: 150 }),
  cellFieldToColumnProps("species.name", { width: 150 }),
  cellFieldToColumnProps("database.mnemonic", { width: 150 }),
  cellFieldToColumnProps("geneType", { width: 120 }),
  cellFieldToColumnProps("synonyms", { width: 200 }),
  cellFieldToColumnProps("variantAnalysisGroup.name", { minWidth: 200, width: 250 }),
  cellFieldToColumnProps("profile.symbol", { minWidth: 200, width: 250 }),

  cellFieldToColumnProps("region.contig.sequenceId", { width: 120 }),
  cellFieldToColumnProps("region.range.start", { width: 90 }),
  cellFieldToColumnProps("region.range.stop", { width: 90 }),
  cellFieldToColumnProps("region.strand", { width: 80 }),
  cellFieldToColumnProps("refRegion.contig.sequenceId", { width: 120 }),
  cellFieldToColumnProps("refRegion.range.start", { width: 90 }),
  cellFieldToColumnProps("refRegion.range.stop", { width: 90 }),
  cellFieldToColumnProps("refRegion.strand", { width: 80 }),

  cellFieldToColumnProps("transcripts.accession", { width: 200 }),
  cellFieldToColumnProps("transcripts.name", { width: 200 }),
  cellFieldToColumnProps("proteins.accession", { width: 200 }),
  cellFieldToColumnProps("proteins.name", { width: 200 }),
  cellFieldToColumnProps("proteins.proteinNames", { width: 200 }),

  cellFieldToColumnProps("protein.accession", { width: 130 }),
  cellFieldToColumnProps("protein.systematicName", { width: 130 }),
  cellFieldToColumnProps("protein.description", { width: 200 }),
  cellFieldToColumnProps("protein.genome.name", { width: 150 }),
  cellFieldToColumnProps("protein.genome.species.name", { width: 150 }),
  cellFieldToColumnProps("protein.database.mnemonic", { width: 150 }),

  cellFieldToColumnProps("transcript.accession", { width: 130 }),
  cellFieldToColumnProps("transcript.name", { width: 130 }),
  cellFieldToColumnProps("transcript.description", { width: 200 }),
  cellFieldToColumnProps("transcript.genome.name", { width: 150 }),
  cellFieldToColumnProps("transcript.genome.species.name", { width: 150 }),
  cellFieldToColumnProps("transcript.database.mnemonic", { width: 150 }),

  cellFieldToColumnProps("variantType", { minWidth: 120, width: 130 }),
  cellFieldToColumnProps("refGenome.name", { minWidth: 130, width: 150 }),
  cellFieldToColumnProps("effect", { minWidth: 180, width: 200 }),
  cellFieldToColumnProps("refAllele", { minWidth: 110, width: 100, maxWidth: 200 }),
  cellFieldToColumnProps("altAllele", { minWidth: 110, width: 100, maxWidth: 200 }),
  cellFieldToColumnProps("locationClass", { minWidth: 140, width: 150 }),

  cellFieldToColumnProps("biologicalProcess", { minWidth: 200, width: 300 }),
  cellFieldToColumnProps("cellularComponent", { minWidth: 200, width: 300 }),
  cellFieldToColumnProps("molecularFunction", { minWidth: 200, width: 300 }),

  cellFieldToColumnProps("genomeStatistics.ecCoverage", {
    valueFormatter: (params) => formatToPercentage(params.value?.toFixed(2)),
  }),
  cellFieldToColumnProps("bestHsp.queryCoverage", { valueFormatter: _getPercentageValueFormatter }),
  cellFieldToColumnProps("bestHsp.ident", { valueFormatter: _getPercentageValueFormatter }),
  cellFieldToColumnProps("queryCoverage", { valueFormatter: _getPercentageValueFormatter }),
  cellFieldToColumnProps("ident", { valueFormatter: _getPercentageValueFormatter }),
  cellFieldToColumnProps("protein.gc", { valueFormatter: _getPercentageValueFormatter }),
  cellFieldToColumnProps(BLAST_RESULT_FIELDS.HIT_QUERY_COVER, { valueFormatter: _getPercentageValueFormatter }),
  cellFieldToColumnProps(BLAST_RESULT_FIELDS.HIT_IDENT, { valueFormatter: _getPercentageValueFormatter }),

  cellFieldToColumnProps("transcriptLocations.region.contig.sequenceId", { width: 120 }),
  cellFieldToColumnProps("transcriptLocations.region.range.start", { width: 90 }),
  cellFieldToColumnProps("transcriptLocations.region.range.stop", { width: 90 }),
  cellFieldToColumnProps("transcriptLocations.region.strand", { width: 80 }),

  cellFieldToColumnProps("proteinLocations.region.contig.sequenceId", { width: 120 }),
  cellFieldToColumnProps("proteinLocations.region.range.start", { width: 90 }),
  cellFieldToColumnProps("proteinLocations.region.range.stop", { width: 90 }),
  cellFieldToColumnProps("proteinLocations.region.strand", { width: 80 }),

  // Following two settings apply exclusively in the admin interface
  cellFieldToColumnProps("playbook.name", { minWidth: 80, width: 250 }),
  cellFieldToColumnProps("category.imageName", { minWidth: 50, width: 100, maxWidth: 100 }),

  cellFieldToColumnProps("rank", { minWidth: 90, width: 90, maxWidth: 90 }),
  cellFieldToColumnProps("target.description", { minWidth: 200, width: 250 }),
  cellFieldToColumnProps("target.accession", { minWidth: 120, width: 130 }),
  cellFieldToColumnProps("bestHsp.alignmentLength", { minWidth: 120, width: 130, maxWidth: 150 }),
  cellFieldToColumnProps("bestHsp.targetStart", { minWidth: 80, width: 90, maxWidth: 100 }),
  cellFieldToColumnProps("bestHsp.targetStop", { minWidth: 80, width: 90, maxWidth: 100 }),
  cellFieldToColumnProps("bestHsp.relatedGenes.name", { minWidth: 80, width: 90 }),
  cellFieldToColumnProps("bestHsp.relatedGenes.strand", { width: 80 }),
  cellFieldToColumnProps("bestHsp.relatedGenes.sequence", { minWidth: 80, width: 90 }),
  cellFieldToColumnProps("hspCount", { minWidth: 90, width: 90, maxWidth: 90 }),

  cellFieldToColumnProps("project.name", { minWidth: 80, width: 280 }),
  cellFieldToColumnProps("project.description", { minWidth: 200, width: 250, maxWidth: 500 }),

  cellFieldToColumnProps("projectMemberActions", { minWidth: 100, width: 100, maxWidth: 100 }),
  cellFieldToColumnProps("editAction", { minWidth: 40, width: 40, maxWidth: 40 }),

  cellFieldToColumnProps(BLAST_RESULT_FIELDS.QUERY_ACCESSION, {
    minWidth: 150,
    width: 220,
    cellClass: "query-cell-w-bg",
  }),
  cellFieldToColumnProps(BLAST_RESULT_FIELDS.QUERY_DESCRIPTION, {
    minWidth: 200,
    width: 300,
    cellClass: "query-cell-w-bg",
  }),
  cellFieldToColumnProps(BLAST_RESULT_FIELDS.HIT_ACCESSION, { minWidth: 150, width: 180 }),

  cellFieldToColumnProps("vocabularyTerms.name", {
    cellClass: (params) =>
      !cellParamsMatchesAttributeTypes(params.data, [
        ATTRIBUTE_TYPE.DYNAMIC_TERMS,
        ATTRIBUTE_TYPE.DERIVED_TERMS,
        ATTRIBUTE_TYPE.CONTROLLED_TEXT,
      ]) && "not-applicable-cell",
  }),
  cellFieldToColumnProps("minValue", {
    cellClass: (params) =>
      !cellParamsMatchesAttributeTypes(params.data, [
        ATTRIBUTE_TYPE.FREE_NUMERICAL,
        ATTRIBUTE_TYPE.CONTROLLED_NUMERICAL,
      ]) && "not-applicable-cell",
  }),
  cellFieldToColumnProps("maxValue", {
    cellClass: (params) =>
      !cellParamsMatchesAttributeTypes(params.data, [
        ATTRIBUTE_TYPE.FREE_NUMERICAL,
        ATTRIBUTE_TYPE.CONTROLLED_NUMERICAL,
      ]) && "not-applicable-cell",
  }),
  cellFieldToColumnProps("maxLength", {
    cellClass: (params) =>
      !cellParamsMatchesAttributeTypes(params.data, [ATTRIBUTE_TYPE.FREE_TEXT, ATTRIBUTE_TYPE.HYPERLINK]) &&
      "not-applicable-cell",
  }),
  cellFieldToColumnProps("decimals", {
    cellClass: (params) =>
      !cellParamsMatchesAttributeTypes(params.data, [
        ATTRIBUTE_TYPE.FREE_NUMERICAL,
        ATTRIBUTE_TYPE.CALCULATED,
        ATTRIBUTE_TYPE.CONTROLLED_NUMERICAL,
      ]) && "not-applicable-cell",
  }),
  cellFieldToColumnProps("pattern", {
    cellClass: (params) =>
      !cellParamsMatchesAttributeTypes(params.data, [ATTRIBUTE_TYPE.FREE_TEXT, ATTRIBUTE_TYPE.HYPERLINK]) &&
      "not-applicable-cell",
  }),
  cellFieldToColumnProps("patternHint", {
    cellClass: (params) =>
      !cellParamsMatchesAttributeTypes(params.data, [ATTRIBUTE_TYPE.FREE_TEXT, ATTRIBUTE_TYPE.HYPERLINK]) &&
      "not-applicable-cell",
  }),
  cellFieldToColumnProps("formulaText", {
    cellClass: (params) =>
      !cellParamsMatchesAttributeTypes(params.data, [ATTRIBUTE_TYPE.CALCULATED]) && "not-applicable-cell",
  }),
  cellFieldToColumnProps("sqlStatement", {
    cellClass: (params) =>
      !cellParamsMatchesAttributeTypes(params.data, [ATTRIBUTE_TYPE.DERIVED_TERMS, ATTRIBUTE_TYPE.DYNAMIC_TERMS]) &&
      "not-applicable-cell",
  }),
  cellFieldToColumnProps("idAccessor", {
    cellClass: (params) =>
      !cellParamsMatchesAttributeTypes(params.data, [ATTRIBUTE_TYPE.DERIVED_TERMS, ATTRIBUTE_TYPE.DYNAMIC_TERMS]) &&
      "not-applicable-cell",
  }),
  cellFieldToColumnProps("coloringRule", {
    cellClass: (params) =>
      (!cellParamsMatchesAttributeTypes(params.data, [
        ATTRIBUTE_TYPE.CALCULATED,
        ATTRIBUTE_TYPE.FREE_NUMERICAL,
        ATTRIBUTE_TYPE.FREE_TEXT,
        ATTRIBUTE_TYPE.CONTROLLED_TEXT,
        ATTRIBUTE_TYPE.BOOLEAN,
        ATTRIBUTE_TYPE.DATE,
        ATTRIBUTE_TYPE.DYNAMIC_TERMS,
      ]) ||
        isMultiValue(params.data as Attribute) ||
        isRepresenterOrUsageAttribute(params.data as Attribute)) &&
      "not-applicable-cell",
  }),
  //cellFieldToColumnProps("representedColumn", {cellClass: (params) => !cellParamsMatchesAttributeTypes(params.data, [ATTRIBUTE_TYPE.FREE_TEXT]) && "not-applicable-cell"}),

  cellFieldToColumnProps("bestBlastResults.alignment", {
    minWidth: BEST_HIT_COLUMN_WIDTH,
    width: BEST_HIT_COLUMN_WIDTH,
    maxWidth: BEST_HIT_COLUMN_WIDTH,
  }),

  trackerAttributeTypeToColumnProps(ATTRIBUTE_TYPE.CALCULATED, {
    cellClass: (params: CellClassParams) => (params.value == undefined ? "missing-data" : null),
    tooltipValueGetter: (params: ITooltipParams) =>
      params.value == undefined ? "This formula has not been resolved yet" : null,
    // version with formatted number as valueGetter since this is copied to the clipboard
    valueGetter: _getTrackerAttributeNumberValueGetter,
  }),

  trackerAttributeTypeToColumnProps(ATTRIBUTE_TYPE.FREE_NUMERICAL, {
    // version with formatted number as valueGetter since this is copied to the clipboard
    valueGetter: _getTrackerAttributeNumberValueGetter,
  }),
];

// see https://www.ag-grid.com/javascript-grid-column-definitions/#default-column-definitions
export const gridColumnTypes: Dictionary<ICustomColDef> = {
  [CELL_TYPE.CHECKBOX]: {
    headerName: "",
    field: CHECKBOX_COLUMN,
    colId: CHECKBOX_COLUMN,
    width: 40,
    minWidth: 40,
    maxWidth: 40,
    pinned: true,
    lockPosition: true,
    suppressMovable: true,
    suppressMenu: true,
    suppressSizeToFit: true,
  },
  [CELL_TYPE.BOOLEAN]: {
    minWidth: 40,
    width: 110,
    suppressSizeToFit: true,
  },
  [CELL_TYPE.FILE]: {
    minWidth: 40,
    width: 110,
  },
  [CELL_TYPE.TEXT]: { minWidth: 200, width: 300 },
  [CELL_TYPE.STRING]: {
    minWidth: 100,
    width: 130,
  },
  [CELL_TYPE.STRING_LIST]: {
    valueParser: (params: ValueParserParams) => {
      if (Array.isArray(params.newValue)) {
        return params.newValue;
      } else {
        return [params.newValue]; // for editing a string_list value
      }
    },
  },
  [CELL_TYPE.NUMBER]: {
    minWidth: 60,
    width: 80,
    valueParser: (params: ValueParserParams) => (!params.newValue?.length ? null : params.newValue), // convert empty input to null
  },
  [CELL_TYPE.INTEGER]: {
    minWidth: 60,
    width: 80,
    valueParser: (params: ValueParserParams) => (!params.newValue?.length ? null : params.newValue), // convert empty input to null
  },
  [CELL_TYPE.SCIENTIFIC]: {
    minWidth: 80,
    width: 100,
  },
  [CELL_TYPE.DATE]: {
    minWidth: 90,
    width: 100,
    suppressSizeToFit: true,
  },
  [CELL_TYPE.DATE_TIME]: {
    minWidth: 160,
    width: 160,
    suppressSizeToFit: true,
  },
  [CELL_TYPE.VARIANT_PROFILE]: {},
  [CELL_TYPE.FILENAME]: {},
};

/**
 * Generic functions used internally to generate the getters and formatters for data and date_time columns
 * This is important to do it here for formatting the data before it is copied to the clipboard from the cell.
 * Another option would be to use the ag-grid prop processCellForClipboard, leaving the formatting up to the cell (or valueFormatter)
 */
/*
function _getDateValueGetter(options?) {
  return (params: ValueGetterParams): string[] | string | undefined => {
    if (params.data) {
      const data = defaultValueGetter(params);
      const multiValues = Array.isArray(data) ? data : [data];
      return multiValues.map((v) => formatDateTimeFromISO(v, options));
    }
  };
}
*/
function _getTrackerAttributeNumberValueGetter(params: ValueGetterParams): string[] | string | undefined {
  if (params.data) {
    const data = defaultValueGetter(params);
    const colDef: IColumnDef = params.colDef;
    if (colDef.attribute?.decimals != null && colDef.attribute.decimals >= 0) {
      const multiValues = Array.isArray(data) ? data : [data];
      return multiValues.map((v) => {
        //! Casting `undefined` and `null` will give '0' in JS.
        // If it fails to cast, NaN.toFixed() returns NaN, NaN being a number.
        const value = typeof v === "string" ? Number(v) : v;
        return value?.toFixed(colDef.attribute.decimals);
      });
    }
    return data;
  }
}

function _getPercentageValueFormatter(params: ValueFormatterParams): string | undefined {
  return formatToPercentage(params.value);
}

function formatToPercentage(value: string) {
  if (value !== null && value !== undefined) {
    //   About the space before the percent sign:https://english.stackexchange.com/questions/3281/should-there-be-a-space-before-a-percent-sign
    return `${value} %`;
  }
}

/**
 * Traverses into an object along the path to get values given a field path, but supporting arrays as part of the path.
 * With throwError=true it raises MissingCellDataError. This is required for cell renderers, otherwise whole grid is broken.
 * It returns a list of all collected values.
 * @param object      a object, may contain arrays along the path.
 * @param path        an access path like in _.get. eg: 'down.anArray.andAnEntity.withAnAttribute'
 * @param throwError  ask for verification. can raise MissingCellDataError for grid
 * @param cellExpanded
 * @return            list of values
 */
export function traverseAndCollect(object: any, path: string, throwError = false) {
  if (!path && throwError) {
    // verification applies only in scope of cell renderers, otherwise whole grid is broken
    throw new MissingCellDataError(); // no field or no groupField when needed
  }
  return traverseAndCollectRecursive(object, path, throwError);
}

function traverseAndCollectRecursive(object: any, path: string, throwError: boolean) {
  const pathElements: string[] = path.split(".");
  const length = pathElements.length;
  let index = 0;

  while (object !== null && object !== undefined && index < length) {
    if (Array.isArray(object)) {
      // Path element specifying a value within an array
      const subPath: string = pathElements.slice(index++).join(".");
      object = object.map((e) => traverseAndCollectRecursive(e, subPath, throwError));
      break;
    } else {
      const pathElement: string = pathElements[index++];
      // verification applies only in scope of cell renderers, otherwise whole grid is broken
      if (throwError && !object.hasOwnProperty(pathElement)) {
        // verification applies only in scope of cell renderers, otherwise whole grid is broken
        throw new MissingCellDataError(); // mismatch between the colDef configuration (field/groupField) and the data
      }
      // Standard path element specifying a value within an object
      object = object[pathElement];
    }
  }
  return index ? object : undefined;
}

export const getItemsByPattern = (field, pattern, data) => {
  const match: any = field.match(pattern);
  //const groupField: string = match?.groups?.groupField; => using regex named group feature (not supported in all browsers yet)
  const groupField: string = match?.length && match[1]; // [0] is the full match
  return traverseAndCollect(data, groupField, true);
};
